/**
 * Owner session client — the four Better Auth REST calls the console needs.
 * Cookies are same-origin in prod and ride the Vite proxy in dev, so
 * `credentials: 'include'` covers both.
 */
import type { ProfileColorId, SessionUser } from '@tv-casino/shared';
import { referralRef } from './storage.js';

export type { SessionUser };

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

/** Null when signed out. */
export async function getSession(): Promise<SessionUser | null> {
  const res = await fetch('/api/auth/get-session', { credentials: 'include' });
  if (!res.ok) return null;
  const data = (await res.json()) as { user?: SessionUser } | null;
  return data?.user ?? null;
}

const fail = async (res: Response): Promise<never> => {
  let message = 'Something went wrong. Try again.';
  try {
    const body = (await res.json()) as { message?: string };
    if (body.message) message = body.message;
  } catch {
    // keep the fallback
  }
  throw new Error(message);
};

export async function signUp(
  email: string,
  password: string,
  name: string,
  playerName?: string,
): Promise<SessionUser> {
  // The code this browser arrived on, if any. The server only looks it up,
  // so a stale or unknown one opens the account with no referrer.
  const ref = referralRef.get() ?? undefined;
  const res = await post('/api/auth/sign-up/email', { email, password, name, playerName, ref });
  if (!res.ok) await fail(res);
  referralRef.clear();
  const created = await getSession();
  if (!created) throw new Error('Signed up, but the session did not stick.');
  return created;
}

export async function signIn(email: string, password: string): Promise<SessionUser> {
  const res = await post('/api/auth/sign-in/email', { email, password });
  if (!res.ok) await fail(res);
  const session = await getSession();
  if (!session) throw new Error('Signed in, but the session did not stick.');
  return session;
}

export async function signOut(): Promise<void> {
  await post('/api/auth/sign-out', {});
}

/**
 * Edit the account's profile. Each field is sent only when it is present, so
 * a colour change never restates a name the caller never showed — and an
 * empty `playerName` releases the reservation, which only exists while a name
 * is set.
 */
export async function updateProfile(patch: {
  playerName?: string;
  profileColor?: ProfileColorId | null;
}): Promise<SessionUser> {
  const res = await fetch('/api/me', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  });
  if (!res.ok) await fail(res);
  return (await res.json()) as SessionUser;
}

/** Claim the name this account plays under. */
export const setPlayerName = (playerName: string): Promise<SessionUser> =>
  updateProfile({ playerName });
